import { AccountBalance, Program } from '@monorepo/types';
import {
  DailyLimitCalculation,
  DailyLimitConfig,
  DailyLimitType,
} from '../types/daily-limit';

/**
 * Determines the daily limit configuration from program settings
 * Priority: dailyLossPercentage > dailyPausePercentage > dailyPause
 */
export const getDailyLimitConfig = (
  program: Program | undefined | null,
): DailyLimitConfig | null => {
  if (!program?.config) return null;

  const { dailyLossPercentage, dailyPausePercentage, dailyPause } =
    program.config;

  // Check for daily loss percentage (highest priority)
  if (dailyLossPercentage !== undefined && dailyLossPercentage > 0) {
    return {
      type: DailyLimitType.DailyLoss,
      percentage: dailyLossPercentage,
      isPercentageBased: true,
    };
  }

  // Check for daily pause percentage
  if (dailyPausePercentage !== undefined && dailyPausePercentage > 0) {
    return {
      type: DailyLimitType.DailyPause,
      percentage: dailyPausePercentage,
      isPercentageBased: true,
    };
  }

  // Check for daily pause fixed amount
  if (dailyPause !== undefined && dailyPause > 0) {
    return {
      type: DailyLimitType.DailyPause,
      fixedAmount: dailyPause,
      isPercentageBased: false,
    };
  }

  return null;
};

/**
 * Calculates the base balance with credits for daily limit calculations
 */
export const calculateBaseBalanceWithCredits = (
  program: Program | undefined | null,
  accountBalance: AccountBalance | undefined,
): number => {
  const buyingPower = program?.config?.buyingPower || 0;
  const credits = accountBalance?.credits || 0;
  return buyingPower + credits || buyingPower;
};

/**
 * Gets the maximum snapshot value (midnight balance/equity or base balance)
 */
export const getMaxSnapshot = (
  accountBalance: AccountBalance | undefined,
  baseBalanceWithCredits: number,
): number => {
  const snapshotBalance = accountBalance?.snapshotBalance;
  const snapshotEquity = accountBalance?.snapshotEquity;

  if (!snapshotBalance || !snapshotEquity) {
    return baseBalanceWithCredits;
  }

  return Math.max(snapshotBalance, snapshotEquity);
};

/**
 * Gets the current equity or base balance for calculations
 */
export const getBaseEquity = (
  accountBalance: AccountBalance | undefined,
  baseBalanceWithCredits: number,
): number => {
  const snapshotBalance = accountBalance?.snapshotBalance;
  const snapshotEquity = accountBalance?.snapshotEquity;
  const currentEquity = accountBalance?.equity || 0;

  if (snapshotBalance === undefined || snapshotEquity === undefined) {
    return baseBalanceWithCredits;
  }

  return currentEquity;
};

/**
 * Calculates the remaining amount before hitting daily limit
 */
export const calculateLossLeft = (
  accountBalance: AccountBalance | undefined,
  program: Program | undefined | null,
): number => {
  const allowedLoss = (accountBalance?.allowedDailyLosses || 0) * -1;
  const baseBalanceWithCredits = calculateBaseBalanceWithCredits(
    program,
    accountBalance,
  );
  const maxSnapshot = getMaxSnapshot(accountBalance, baseBalanceWithCredits);
  const baseEquity = getBaseEquity(accountBalance, baseBalanceWithCredits);
  const dailyLimitLevel = maxSnapshot - allowedLoss;

  return Math.max(0, baseEquity - dailyLimitLevel);
};

/**
 * Calculates the daily limit amount based on configuration and account balance
 */
export const calculateDailyLimitAmount = (
  config: DailyLimitConfig,
  accountBalance: AccountBalance | undefined,
  program: Program | undefined | null,
): number => {
  if (!accountBalance) return 0;

  if (config.isPercentageBased && config.percentage) {
    // For percentage-based limits, calculate percentage from base balance

    const value =
      ((program?.config?.buyingPower || 0) * config.percentage) / 100;

    return value;
  }

  // For fixed amount limits
  return config.fixedAmount || 0;
};

/**
 * Performs complete daily limit calculation including current status
 */
export const calculateDailyLimit = (
  config: DailyLimitConfig | null,
  accountBalance: AccountBalance | undefined,
  program: Program | undefined | null,
): DailyLimitCalculation | null => {
  if (!config || !accountBalance) return null;

  const currentValue = Math.abs(accountBalance.dailyProfitAndLoss || 0);
  const limitValue = calculateDailyLimitAmount(config, accountBalance, program);
  const remainingAmount = calculateLossLeft(accountBalance, program);
  const isOverLimit = remainingAmount <= 0;

  return {
    currentValue,
    limitValue,
    remainingAmount,
    isOverLimit,
  };
};

/**
 * Gets the translation key for daily limit type
 */
export const getDailyLimitTranslationKey = (
  limitType: DailyLimitType,
): string => {
  return limitType === DailyLimitType.DailyLoss
    ? 'objectives.dailyLoss'
    : 'objectives.dailyPause';
};
